import { useEffect, useState } from "react";
import { X, ArrowRight, ArrowLeft, Check, Store, DollarSign, Megaphone, Package, Copy, Sparkles, ExternalLink, ChevronRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

const STEPS = [
  { key: "handle", label: "Handle", title: "Pick your storefront handle", icon: Store },
  { key: "payments", label: "Payments", title: "How do buyers pay you?", icon: DollarSign },
  { key: "announcement", label: "Announce", title: "Write a shop announcement", icon: Megaphone },
  { key: "product", label: "First product", title: "Add your first product", icon: Package },
  { key: "live", label: "Go live", title: "Go live!", icon: Sparkles },
];

const publicUrl = (username) => `${window.location.origin}/shop/${username}`;

export default function OnboardingWizard({ accent = "#FF7E67", onClose, initialStep = 0, onProductAddClick }) {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(initialStep);
  const [saving, setSaving] = useState(false);

  const sp = user?.seller_profile || {};
  const ob = sp.onboarding || {};
  const completedSteps = new Set(ob.completed_steps || []);
  const storefrontEnabled = user?.baker_location?.storefront_enabled !== false;

  // Handle step state
  const [username, setUsername] = useState(user?.username || "");
  const [savingHandle, setSavingHandle] = useState(false);

  // Payments state
  const [venmo, setVenmo] = useState(sp.payment_venmo || "");
  const [cashapp, setCashapp] = useState(sp.payment_cashapp || "");
  const [zelle, setZelle] = useState(sp.payment_zelle || "");

  // Announcement state
  const [announcement, setAnnouncement] = useState(sp.shop_announcement || "");

  useEffect(() => {
    setUsername(user?.username || "");
    setVenmo(sp.payment_venmo || "");
    setCashapp(sp.payment_cashapp || "");
    setZelle(sp.payment_zelle || "");
    setAnnouncement(sp.shop_announcement || "");
    // eslint-disable-next-line
  }, [user?.username, sp.payment_venmo, sp.payment_cashapp, sp.payment_zelle, sp.shop_announcement]);

  const markStepDone = async (key) => {
    try {
      await api.patch("/seller/onboarding", { completed_step: key });
    } catch (e) { /* non-fatal */ }
  };

  const goNext = async () => {
    const key = STEPS[step].key;
    await markStepDone(key);
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  const skip = async () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else await complete();
  };

  const complete = async () => {
    setSaving(true);
    try {
      await api.patch("/seller/onboarding", { completed: true });
      await refresh();
      toast.success("You're all set! 🎉");
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const dismissForLater = async () => {
    try {
      await api.patch("/seller/onboarding", { dismissed: true });
      await refresh();
      toast.info("You can restart the wizard anytime from your dashboard.");
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // ---- Step savers ----
  const saveHandle = async () => {
    const clean = username.trim().toLowerCase().replace(/[^a-z0-9_.-]/g, "");
    if (!clean || clean.length < 3) { toast.error("Handle must be at least 3 characters"); return; }
    setSavingHandle(true);
    try {
      if (clean !== user.username) {
        await api.patch("/auth/me", { username: clean });
        toast.success("Handle updated");
      }
      await refresh();
      await markStepDone("handle");
      setStep(1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingHandle(false);
    }
  };

  const savePayments = async () => {
    setSaving(true);
    try {
      await api.patch("/seller/me", {
        business_name: sp.business_name,
        contact_email: sp.contact_email || user.email,
        payment_venmo: venmo.trim() || null,
        payment_cashapp: cashapp.trim() || null,
        payment_zelle: zelle.trim() || null,
      });
      toast.success("Payment methods saved");
      await refresh();
      await markStepDone("payments");
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveAnnouncement = async () => {
    setSaving(true);
    try {
      await api.patch("/seller/me", {
        business_name: sp.business_name,
        contact_email: sp.contact_email || user.email,
        shop_announcement: announcement.trim(),
      });
      toast.success("Announcement saved");
      await refresh();
      await markStepDone("announcement");
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const goLive = async () => {
    setSaving(true);
    try {
      await api.patch("/seller/storefront-enabled", { enabled: true });
      await refresh();
      await markStepDone("live");
      await complete();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSaving(false);
    }
  };

  const copyUrl = () => {
    try {
      navigator.clipboard.writeText(publicUrl(user.username));
      toast.success("URL copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  if (!user) return null;
  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/50 p-4" data-testid="onboarding-wizard">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header + progress */}
        <div className="p-5 border-b border-[#EBDCD1]" style={{ background: `linear-gradient(135deg, ${accent}22 0%, #ffffff 100%)` }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full grid place-items-center text-white" style={{ background: accent }}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#857163]">Storefront setup · Step {step + 1} of {STEPS.length}</div>
                <div className="font-heading text-xl text-[#3B2C24] leading-tight">{current.title}</div>
              </div>
            </div>
            <button onClick={dismissForLater} className="text-[#857163] hover:text-[#3B2C24] p-1" data-testid="onboarding-close" title="Close and come back later">
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Progress bar */}
          <div className="flex items-center gap-1" data-testid="onboarding-progress">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex-1 flex items-center gap-1">
                <div
                  className={`h-1.5 flex-1 rounded-full transition ${i <= step ? "" : "bg-[#EBDCD1]"}`}
                  style={i <= step ? { background: accent } : undefined}
                />
              </div>
            ))}
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[10px] text-[#857163]">
            {STEPS.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setStep(i)}
                className={`px-1 py-0.5 rounded font-semibold uppercase tracking-wider ${i === step ? "text-[#3B2C24]" : "text-[#857163] hover:text-[#3B2C24]"}`}
                data-testid={`onboarding-tab-${s.key}`}
              >
                {completedSteps.has(s.key) ? "✓ " : ""}{s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 0 && (
            <div className="space-y-4" data-testid="step-handle">
              <p className="text-sm text-[#857163]">
                Your handle is the last part of your public storefront URL. Keep it short, lowercase, no spaces. Letters, numbers, dots, dashes, and underscores are OK.
              </p>
              <label className="block">
                <span className="text-xs text-[#857163]">Storefront handle</span>
                <div className="mt-1 flex items-center gap-1">
                  <div className="text-sm text-[#857163] shrink-0">cakepoptalk.com/shop/</div>
                  <input
                    className="cp-input flex-1"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    maxLength={30}
                    placeholder="your-shop"
                    data-testid="onboarding-username-input"
                  />
                </div>
                <div className="text-[11px] text-[#857163] mt-1" data-testid="onboarding-preview-url">
                  Preview: <b className="text-[#3B2C24]">{publicUrl(username || user.username)}</b>
                </div>
              </label>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3" data-testid="step-payments">
              <p className="text-sm text-[#857163]">
                Buyers pay you directly outside the app. Add the payment methods you accept — you can always change these later.
              </p>
              <label className="block">
                <span className="text-xs text-[#857163]">Venmo <span className="text-[#B79885]">(optional)</span></span>
                <input className="cp-input mt-1" value={venmo} onChange={(e) => setVenmo(e.target.value)} placeholder="@yourhandle" data-testid="onboarding-venmo" maxLength={40} />
              </label>
              <label className="block">
                <span className="text-xs text-[#857163]">CashApp $cashtag <span className="text-[#B79885]">(optional)</span></span>
                <input className="cp-input mt-1" value={cashapp} onChange={(e) => setCashapp(e.target.value)} placeholder="$yourtag" data-testid="onboarding-cashapp" maxLength={40} />
              </label>
              <label className="block">
                <span className="text-xs text-[#857163]">Zelle email or phone <span className="text-[#B79885]">(optional)</span></span>
                <input className="cp-input mt-1" value={zelle} onChange={(e) => setZelle(e.target.value)} placeholder="orders@yourbakery.com or (555) 123-4567" data-testid="onboarding-zelle" maxLength={120} />
              </label>
              <div className="text-xs text-[#857163] italic">Skip this step if you'd rather DM buyers with payment details.</div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="step-announcement">
              <p className="text-sm text-[#857163]">
                Shows at the top of your public storefront. Great for turnaround times, holiday hours, or a welcome message.
              </p>
              <label className="block">
                <span className="text-xs text-[#857163]">Shop announcement</span>
                <textarea
                  className="cp-input mt-1 min-h-[100px]"
                  value={announcement}
                  onChange={(e) => setAnnouncement(e.target.value.slice(0, 280))}
                  placeholder="e.g. Now booking orders for December! 5-day turnaround."
                  data-testid="onboarding-announcement"
                />
                <div className="text-right text-[10px] text-[#857163] mt-0.5">{280 - announcement.length} chars left</div>
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="step-product">
              <p className="text-sm text-[#857163]">
                Add your first product listing — a photo, name, and price get you selling. You can add more anytime.
              </p>
              <div className="p-4 rounded-2xl bg-[#FFF9EF] border border-[#EBDCD1]">
                <div className="font-semibold text-sm text-[#3B2C24] mb-2 flex items-center gap-1.5">
                  <Package className="w-4 h-4" style={{ color: accent }} /> Ready to add?
                </div>
                <p className="text-xs text-[#857163] mb-3">
                  Click below to open the product editor. When you're done adding it, come back to finish the wizard.
                </p>
                <button
                  onClick={() => {
                    onProductAddClick?.();
                    markStepDone("product");
                  }}
                  className="cp-btn-primary text-sm inline-flex items-center gap-1.5"
                  data-testid="onboarding-open-product-editor"
                >
                  <Package className="w-4 h-4" /> Open product editor
                </button>
              </div>
              <div className="text-xs text-[#857163] italic">Already added products? Great — just click Next.</div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="step-live">
              <p className="text-sm text-[#857163]">
                Flip the switch to make your storefront live in the Store Fronts directory + on your map pin. Buyers can browse, cart, and check out immediately.
              </p>
              <div className="p-4 rounded-2xl border border-[#EBDCD1] bg-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full grid place-items-center text-white" style={{ background: accent }}>
                  <Store className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#3B2C24]">Your public storefront</div>
                  <div className="text-xs text-[#857163] break-all" data-testid="onboarding-final-url">{publicUrl(user.username)}</div>
                </div>
                <button onClick={copyUrl} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6] inline-flex items-center gap-1" data-testid="onboarding-copy-url">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
              <div className={`p-4 rounded-2xl border-l-4 ${storefrontEnabled ? "bg-[#F0F9EA] border-l-[#5E7A2E]" : "bg-[#FFF9EF] border-l-[#F4A83B]"}`}>
                <div className="text-sm font-semibold text-[#3B2C24]">
                  {storefrontEnabled ? "🎉 Your storefront is already live!" : "Toggle live to publish"}
                </div>
                <div className="text-xs text-[#857163] mt-0.5">
                  {storefrontEnabled
                    ? "Nothing more to do — your shop is discoverable now."
                    : "One tap and buyers can start finding you."}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#EBDCD1] p-4 flex items-center gap-2 flex-wrap bg-white">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="text-sm px-3 py-2 rounded-full bg-white border border-[#EBDCD1] text-[#3B2C24] disabled:opacity-40 inline-flex items-center gap-1" data-testid="onboarding-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <button onClick={skip} className="text-sm px-3 py-2 rounded-full text-[#857163] hover:text-[#3B2C24]" data-testid="onboarding-skip">
            Skip
          </button>
          <div className="flex-1" />
          {step === 0 && (
            <button onClick={saveHandle} disabled={savingHandle} className="cp-btn-primary text-sm px-4 py-2 disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="onboarding-save-handle">
              Save & continue <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
          {step === 1 && (
            <button onClick={savePayments} disabled={saving} className="cp-btn-primary text-sm px-4 py-2 disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="onboarding-save-payments">
              Save & continue <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
          {step === 2 && (
            <button onClick={saveAnnouncement} disabled={saving} className="cp-btn-primary text-sm px-4 py-2 disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="onboarding-save-announcement">
              Save & continue <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
          {step === 3 && (
            <button onClick={goNext} className="cp-btn-primary text-sm px-4 py-2 inline-flex items-center gap-1.5" data-testid="onboarding-next-product">
              Next <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
          {step === 4 && (
            storefrontEnabled ? (
              <button onClick={complete} disabled={saving} className="cp-btn-primary text-sm px-4 py-2 disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="onboarding-finish">
                <Check className="w-3.5 h-3.5" /> Finish
              </button>
            ) : (
              <button onClick={goLive} disabled={saving} className="cp-btn-primary text-sm px-4 py-2 disabled:opacity-60 inline-flex items-center gap-1.5" style={{ background: accent }} data-testid="onboarding-go-live">
                <Sparkles className="w-3.5 h-3.5" /> Go live!
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
